import type { AxiosError } from "axios";
import { useApiClient } from "../apiClients";
import { KNOWLEDGE_ENDPOINTS } from "./endpoints";
import type { UUID } from "../../models/domain";

/**
 * Base de faits typée d'un objectif (étape 4).
 *
 * Contrat : `local/debat-architecture/etapes/04-base-de-faits.md`, annexe A.
 * `unit` contient TOUS les faits ; `disabled` liste ceux à ignorer (c'est
 * tc_learn qui les filtre, l'écran éditorial doit voir les deux).
 */

/**
 * Type EFFECTIF d'une relation (celui sur lequel un leurre se construit).
 *
 * `symmetric` = vraie dans les deux sens ; `asymmetric` = l'inverse est fausse ;
 * `functional` = pour ce `from` et ce `label`, `to` est unique dans le cours ;
 * `undeclared` = le modèle ne s'est engagé sur AUCUN sens (type absent à
 * l'extraction). `undeclared` n'autorise donc ni inversion ni substitution :
 * il ne compte dans aucun trait.
 */
export type RelationType = "functional" | "asymmetric" | "symmetric" | "undeclared";

/**
 * Type DÉCLARÉ à l'extraction, avant la rétrogradation de la relecture.
 * `""` quand le modèle n'a rien déclaré. C'est le seul champ qui distingue
 * une déclaration d'un défaut : la fonctionnalité calculée ne s'appuie que
 * sur des libellés déclarés `functional` au moins une fois (critique §4).
 */
export type DeclaredRelationType = RelationType | "";

export type MisconceptionErrorType = "concept" | "procedure" | "prerequisite";

/**
 * Étendue lue (annexe A). `own` = les seuls faits de l'objectif ; les unités
 * par objectif sont atomiques (2 à 4 termes, 0 à 2 relations) et `neighborhood`
 * sert l'UNION de celles des objectifs qui citent les mêmes passages. L'écran
 * éditorial demande `own` par DÉFAUT : sans ça, l'éditeur corrige des faits
 * portés par un autre objectif sans le savoir, et ne peut pas diagnostiquer
 * « cet objectif n'a rien à lui ».
 */
export type KnowledgeScope = "own" | "neighborhood";

/**
 * Ce que tout fait servi porte en plus de son contenu.
 *
 * `owner` = l'objectif qui porte le fait. Un fait PROPRE garde son identifiant
 * local (`r1`) ; un fait versé par un voisin reçoit un identifiant COMPOSITE
 * `<8 hex de l'owner>:<id local>`. Pour l'UI comme pour tc_learn, un
 * identifiant de fait est OPAQUE : on le renvoie tel quel.
 */
export interface KnowledgeFact {
  id: string;
  owner?: UUID;
  /** Ordinal du passage source : recopié du cours, jamais reformulé. */
  chunk: number;
}

export interface KnowledgeTerm extends KnowledgeFact {
  term: string;
  definition: string;
}

export interface KnowledgeRelation extends KnowledgeFact {
  from: string;
  to: string;
  label: string;
  /** Type effectif : rétrogradé par la relecture le cas échéant. */
  type: RelationType;
  /** Type d'origine (`""` si le modèle n'a rien déclaré). */
  declared_type?: DeclaredRelationType;
  /** La relecture a rendu un verdict sur ce fait (son silence ne vaut rien). */
  reviewed?: boolean;
}

export interface KnowledgeStep extends KnowledgeFact {
  order: number;
  text: string;
}

export interface KnowledgeDate extends KnowledgeFact {
  when: string;
  event: string;
}

export interface KnowledgeMisconception extends KnowledgeFact {
  claim: string;
  correction: string;
  error_type: MisconceptionErrorType;
  /** Présents seulement quand l'idée fausse se formule comme une relation. */
  from?: string;
  to?: string;
  label?: string;
}

export interface KnowledgeCategory extends KnowledgeFact {
  name: string;
  /** Identifiants de termes. */
  members: string[];
}

export interface KnowledgeUnit {
  terms: KnowledgeTerm[];
  relations: KnowledgeRelation[];
  steps: KnowledgeStep[];
  dates: KnowledgeDate[];
  misconceptions: KnowledgeMisconception[];
  categories: KnowledgeCategory[];
}

/** Verdict de la relecture sur la base seule (`knowledge_review/v1`). */
export interface FactReview {
  supported: boolean;
  type_ok: boolean;
  /**
   * Le texte du fait est écrit dans la langue du passage (`knowledge_review/v3`).
   * Absent sur un verdict rendu avant v3.
   */
  language_ok?: boolean;
  /** Type effectif retenu après rétrogradation, quand il y en a eu une. */
  demoted_to?: RelationType;
  note?: string;
}

export interface FactOverride {
  fact_id: string;
  /**
   * Absent de la liste `disabled` de la réponse (y figurer vaut désactivé) ;
   * renvoyé explicitement par le PATCH.
   */
  disabled?: boolean;
  /** `review` | `editor:<user_id>` | `report`. */
  disabled_by: string;
  note?: string | null;
  /**
   * L'override ne désigne plus aucun fait servi : la ré-extraction a renuméroté
   * ou supprimé le fait visé. Il n'est donc PAS appliqué, et la correction
   * éditoriale est à refaire — « à revoir ».
   */
  stale?: boolean;
}

/**
 * Comptes par type EFFECTIF, plus le total de la fonctionnalité CALCULÉE :
 * `functional` compte ce que le modèle a déclaré et que la relecture a laissé,
 * `functional_computed` ce que le code a CONSTATÉ dans l'unité servie. C'est le
 * second qui décide de l'analogie (annexe A, « fonctionnalité calculée »).
 */
export interface KnowledgeRelationStats extends Partial<Record<RelationType, number>> {
  functional_computed?: number;
}

export interface KnowledgeStats {
  terms: number;
  relations: KnowledgeRelationStats;
  /** Nombre de triplets par libellé fonctionnel CALCULÉ (clé normalisée). */
  functional_labels: Record<string, number>;
  steps: number;
  dates: number;
  misconceptions: number;
  categories: number;
}

export interface KnowledgeResponse {
  objective_id: UUID;
  document_id: UUID;
  doc_version_id: UUID;
  /** sha256 des chunks sources + version de prompt : clé du disjoncteur. */
  source_hash: string;
  version: string;
  language: string;
  /** Étendue effectivement servie, à afficher : elle change ce qu'on corrige. */
  scope?: KnowledgeScope;
  /**
   * En-tête `X-Knowledge-Engine` recopié dans l'objet : `version` ne porte que
   * la version de PROMPT, inchangée entre l'extraction par fenêtre et par
   * passage. Sans la version de code, un diagnostic coûte une séance
   * (critique §12).
   */
  engine?: string;
  unit: KnowledgeUnit;
  disabled: FactOverride[];
  review: Record<string, FactReview>;
  stats: KnowledgeStats;
  /** Nombre de signalements « exercice faux » par fait. */
  reports: Record<string, number>;
}

export interface PatchFactPayload {
  disabled: boolean;
  note?: string;
}

export interface ExtractKnowledgeResponse {
  document_id: UUID;
  objectives: number;
  units: number;
  disabled_by_review: number;
  /** Relations dont le type a été affaibli, pas désactivé. */
  demoted_by_review?: number;
  /**
   * Objectifs sautés parce que `source_hash` n'a pas bougé. Un run non forcé
   * qui saute TOUT ne remplace rien : c'est le chiffre qui le dit.
   */
  skipped_unchanged?: number;
  /** Passages dont l'extraction a échoué (troncature, modèle muet). */
  failed_windows?: number;
}

export interface RunKnowledgePayload {
  documents?: UUID[];
  /** Tout le parc éligible. */
  all?: boolean;
  /** Ré-extrait même si l'unité existe déjà pour ce `source_hash`. */
  force?: boolean;
}

export interface RunKnowledgeResponse {
  status: string;
  count?: number;
  force?: boolean;
  message?: string;
}

/**
 * État du run du parc (`GET /v1/knowledge/run`).
 *
 * Le run est long (deux appels par passage plus un par objectif) et deux clics
 * successifs lançaient deux runs concurrents qui se bloquaient document par
 * document. L'écran doit donc pouvoir dire « un run tourne, n documents sur
 * N ». Tous les compteurs sont optionnels : un serveur qui ne sert pas encore
 * cette route (404) vaut « aucun run connu », jamais une erreur.
 */
export interface KnowledgeRunStatus {
  running: boolean;
  /** Documents traités / à traiter. */
  done?: number;
  total?: number;
  /** Documents en échec, comptés mais non bloquants. */
  failed?: number;
  started_at?: string;
  force?: boolean;
  message?: string;
}

export interface KnowledgeReport {
  objective_id: UUID;
  fact_id: string;
  count: number;
  last_at: string;
}

/** Unité vide : évite un `?.` sur chaque liste quand le serveur omet une clé. */
export const EMPTY_KNOWLEDGE_UNIT: KnowledgeUnit = {
  terms: [],
  relations: [],
  steps: [],
  dates: [],
  misconceptions: [],
  categories: [],
};

export const useKnowledgeApi = () => {
  const apiClient = useApiClient("embed");

  /** 404 `no_knowledge` = objectif sans unité : un état, pas une erreur. */
  const getKnowledge = async (
    objectiveId: UUID,
    scope: KnowledgeScope = "own",
  ): Promise<KnowledgeResponse | null> => {
    try {
      const res = await apiClient.get<KnowledgeResponse>(
        KNOWLEDGE_ENDPOINTS.GET(objectiveId),
        { params: { scope } },
      );
      const engine = res.headers?.["x-knowledge-engine"];
      return {
        scope,
        ...res.data,
        engine: typeof engine === "string" ? engine : undefined,
      };
    } catch (err) {
      if ((err as AxiosError)?.response?.status === 404) return null;
      throw err;
    }
  };

  const patchFact = (objectiveId: UUID, factId: string, payload: PatchFactPayload) =>
    apiClient.patch<FactOverride>(
      KNOWLEDGE_ENDPOINTS.PATCH_FACT(objectiveId, factId),
      payload,
    );

  const extractDocument = (documentId: UUID) =>
    apiClient.post<ExtractKnowledgeResponse>(KNOWLEDGE_ENDPOINTS.EXTRACT(documentId), {});

  const runBulk = (payload: RunKnowledgePayload) =>
    apiClient.post<RunKnowledgeResponse>(KNOWLEDGE_ENDPOINTS.RUN_BULK, payload);

  /** 404 = le serveur ne publie pas encore l'état : pas de run connu. */
  const getRunStatus = async (): Promise<KnowledgeRunStatus | null> => {
    try {
      const res = await apiClient.get<KnowledgeRunStatus>(KNOWLEDGE_ENDPOINTS.RUN_STATUS);
      return res.data;
    } catch (err) {
      if ((err as AxiosError)?.response?.status === 404) return null;
      throw err;
    }
  };

  const listReports = (since?: string) =>
    apiClient.get<KnowledgeReport[]>(KNOWLEDGE_ENDPOINTS.REPORTS, {
      params: since ? { since } : undefined,
    });

  return { getKnowledge, patchFact, extractDocument, runBulk, getRunStatus, listReports };
};
